import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Table from 'components/Table';
import { Button, Modal, notification, Popover, Skeleton } from 'antd';
import { Breadcrumbs } from 'components/Atom/breadcumbs';
import { ResetButton, FilterButton } from 'components/Atom/Button';
import { PageHeader } from 'components/Atom/PageHeader';
import { useSelector } from 'react-redux';
import { TIMEZONES, USER_ROLES } from 'utils/constant';
import { useQuery } from 'react-query';
import SearchField from 'components/Atom/SearchField/index';
import { checkUserPermissions, convertToSubscriptionTimezone, handleDateRangeFormat, handleStateByPassData } from 'utils/services';
import { DiffOutlined, ReloadOutlined } from '@ant-design/icons';
import { deleteSingleCustomer, getAllCustomer, getSingleCustomer } from 'api/subscriptions';
import Filter from './assets/filter';
import CreateCustomer from './createCustomer';
import ShortenId from 'components/Atom/ShortenId';

export const Customer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const store = useSelector((state) => state.company.store);
  const [search, setSearch] = useState('');
  const [filterToggle, setFilterToggle] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [queryParams, setQueryParams] = useState({
    offset: 0,
    limit: 20,
    search: '',
    currentPage: 1,
  });
  const [trigerApi, setTrigerApi] = useState(true);
  const [delayTimer, setDelayTimer] = useState();
  const tableContainerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [customerModalData, setCustomerModalData] = useState({});
  const [isCustomerDeleting, setIsCustomerDeleting] = useState(false);
  const [deleteCustomerModal, setDeleteCustomerModal] = useState(false);
  const [recordId, setRecordId] = useState('');

  const filterToggler = () => {
    setFilterToggle(true);
  };
  const handleReset = () => {
    setQueryParams((prev) => ({ ...prev, offset: 0, currentPage: 1, filtersData: {} }));
    setTrigerApi(!trigerApi);
    setIsFilterModalOpen(false);
  };

  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Customers',
      },
    ],
  };

  const getCustomerListData = async (queryParams) => {
    try {
      const paramsData = { offset: queryParams?.offset, limit: queryParams?.limit };
      if (queryParams?.search !== '') {
        paramsData['search_key'] = queryParams?.search?.trim();
      }
      const formattedData = filtersDataValues(queryParams?.filtersData);
      const params = { ...formattedData, ...paramsData, ...(store?.isSelected && { store_id: store?._id }), xap_request: true };
      const response = await getAllCustomer(params, store?._id);
      const timezone = store?.settings?.timezone || TIMEZONES?.PKT;
      const result = await convertToSubscriptionTimezone(response?.data?.data, timezone, 'customers');
      response.data.data = result;
      return response?.data?.data;
    } catch (err) {
      console.log(err);
    }
  };
  const {
    data: customerData,
    refetch,
    isLoading: isFetching,
  } = useQuery('customerDatalist', () => getCustomerListData(queryParams), {
    enabled: false,
    manual: true,
  });

  useEffect(() => {
    refetch();
  }, [store, trigerApi]);

  const filtersDataValues = (data) => {
    let filtersData = {};
    if (data?.date_range) {
      const timezone = store?.isSelected ? store?.settings?.timezone || TIMEZONES.PKT : null;
      const formattedDateRange = handleDateRangeFormat(data?.date_range, timezone);
      if (formattedDateRange) {
        filtersData.startTime = formattedDateRange.start_date;
        filtersData.endTime = formattedDateRange.end_date;
      }
    }
    filtersData.status = data?.subscription_status ?? null;
    return filtersData;
  };
  const getSearch = async (e) => {
    if (e === '' || e == null) {
      notification?.error({ message: 'Please enter details for search', placement: 'bottomRight' });
    } else {
      setQueryParams((pre) => ({ ...pre, currentPage: 1, offset: 0, search: e }));
      setTrigerApi(!trigerApi);
    }
  };
  const handleCloseFilterModel = () => {
    setIsFilterModalOpen(!isFilterModalOpen);
  };
  const handleFiltersSearch = (data) => {
    setQueryParams((pre) => ({ ...pre, offset: 0, currentPage: 1, filtersData: data }));
    setTrigerApi(!trigerApi);
  };
  const handlePagePageChange = (page, pageSize) => {
    if (queryParams?.limit !== pageSize) {
      setQueryParams((pre) => ({ ...pre, limit: pageSize, offset: 0, currentPage: 1 }));
      setTrigerApi(!trigerApi);
    } else {
      const countOffset = page * queryParams?.limit - queryParams?.limit;
      setQueryParams((pre) => ({ ...pre, limit: pageSize, offset: countOffset, currentPage: page }));
      setTrigerApi(!trigerApi);
    }
  };
  const handleSearchData = () => {
    if (delayTimer) {
      clearTimeout(delayTimer);
    }
    const newTimer = setTimeout(() => {
      handleRefresh();
    }, 1500);
    setDelayTimer(newTimer);
  };
  const handleRefresh = (isRefresh = true) => {
    if (isRefresh) {
      setSearch(null);
    } else {
      setIsFilterModalOpen(false);
    }
    setQueryParams((pre) => ({ ...pre, offset: 0, currentPage: 1, search: '' }));
    setTrigerApi(!trigerApi);
  };
  const handleSearch = (e) => {
    if (delayTimer && e !== '' && e !== null) {
      clearTimeout(delayTimer);
    }
    setSearch(e?.target?.value);
  };

  const handleOpen = () => {
    setOpen(!open);
    setIsEdit(false);
  };
  const handleDeleteCustomer = async (id) => {
    setIsCustomerDeleting(true);
    const response = await deleteSingleCustomer(customerModalData?.store_id, id);
    if (response?.data?.success) {
      setDeleteCustomerModal(false);
      refetch();
    }
    setIsCustomerDeleting(false);
  };
  const fetchSingleCustomerData = async (customerData) => {
    setDataLoading(true);
    const response = await getSingleCustomer(customerData?.store_id, customerData?._id);
    const data = response?.data?.data;
    setCustomerModalData(data);
    setDataLoading(false);
  };

  useEffect(() => {
    if (location?.state?.isRedirection && customerData) {
      setSearch(location?.state?.searchValue);
      setRecordId(location?.state?.id);
      navigate(location.pathname, { replace: true });
      const data = handleStateByPassData(location?.state?.filtersData, true);
      if (!data) {
        refetch();
      }
      setQueryParams(data);
      setTimeout(() => {
        if (tableContainerRef?.current && location?.state?.id) {
          const row = document?.querySelector(`.custom-row-class.highlighted-row`);
          if (row) {
            row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }, 10);
      return;
    } else if (location?.state?.isRedirection) {
      navigate(location.pathname, { replace: true });
    }
    refetch();
  }, [trigerApi, store]);

  const handleRowHover = () => {
    setRecordId('');
  };

  const customerListColumns = [
    {
      title: 'Customer ID',
      render: (value) => <ShortenId value={value?._id} />,
      key: 'id',
    },
    {
      title: 'Name',
      render: (value) => value?.name,
      key: 'name',
    },
    {
      title: 'Email',
      render: (value) => value?.email || '-',
      key: 'email',
    },
    {
      title: 'Phone',
      render: (value) => value?.phone || '-',
      key: 'phone',
    },
    {
      title: 'Last updated',
      render: (value) => value?.updatedAt,
      key: 'updatedAt',
    },
    ...(checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN, USER_ROLES.XPAY_SUBSCRIPTION_USER])
      ? [
          {
            title: 'Action',
            key: 'action',
            render: (customerData, index) => {
              return (
                <div key={index} className='flex items-center gap-2'>
                  <>
                    <div
                      className='table-action-btn flex items-center justify-center'
                      onClick={() => {
                        fetchSingleCustomerData(customerData);
                        setOpen(true);
                        setIsEdit(true);
                      }}
                      style={{ cursor: 'pointer' }}>
                      Update
                    </div>

                    <div
                      type='primary'
                      className='table-action-btn'
                      onClick={() => {
                        setCustomerModalData(customerData);
                        setDeleteCustomerModal(true);
                      }}
                      style={{ cursor: 'pointer' }}>
                      Delete
                    </div>
                  </>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const popOverContent = (
    <div className='popOver'>
      <div className='popover_header mt-2 mb-3 flex items-center gap-2'>
        <span style={{ fontWeight: '700', color: '#262626' }}>Filter</span>
        <div className='flex items-center text-[#000000] gap-2 cursor-pointer' onClick={() => handleReset()}>
          <ReloadOutlined style={{ fontSize: '16px' }} />
          <span style={{ color: '#000000', fontSize: '14px', lineHeight: '24px' }}>Reset All</span>
        </div>
      </div>
      <div className='intentFilters'>
        <Filter filterToggler={filterToggler} filtersData={queryParams?.filtersData} onSearch={handleFiltersSearch} handleClose={handleCloseFilterModel} />
      </div>
    </div>
  );

  return (
    <>
      <div className='xshield' style={{ padding: '0px', width: '100%' }}>
        <div className='header-container'>
          <Breadcrumbs {...breadcrumbProps} />
          <div className='headerPage'>
            <div className=''>
              <PageHeader label={'Customers'} button={<ResetButton onClick={isFetching ? '' : () => refetch()} loading={isFetching} />} />
            </div>

            {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN, USER_ROLES.XPAY_SUBSCRIPTION_USER]) && (
              <div className='flex txn-btn'>
                <div>
                  <Button
                    className='primaryBtn mr-0 rounded-lg create_button'
                    onClick={() => {
                      handleOpen();
                    }}>
                    <span className='text-base'>Create Customer</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='action_Panel headerContainer' style={{ width: '100%', marginTop: '24px', marginBottom: '20px' }}>
          <div className='headerContainer' style={{ width: '100%', gap: '0px' }}>
            <Popover
              className={isFilterModalOpen ? 'filter-active filterPopover' : filterToggle ? 'text-base activeFilterToggler filterPopover' : 'text-base filterPopover'}
              trigger='click'
              placement='bottomLeft'
              content={popOverContent}
              open={isFilterModalOpen}
              destroyTooltipOnHide
              onOpenChange={handleCloseFilterModel}>
              <FilterButton onClick={() => setIsFilterModalOpen(true)}>
                <div className='text-base'>Filter</div>
              </FilterButton>
            </Popover>
            <div className='searchInput'>
              <SearchField
                handleOnChange={handleSearch}
                value={search}
                handlePressEnter={getSearch}
                onReset={handleSearchData}
                placeholder={`Search by Name, Email, Phone`}
                loading={!!(isFetching && search)}
                dropdown={{ show: false }}
                enableSearchBtn={true}
                style={{ width: '360px' }}
              />
            </div>
          </div>
        </div>
        <div className='overflow-x-auto custom-table' ref={tableContainerRef}>
          <Table
            loading={isFetching}
            columns={customerListColumns}
            dataSource={customerData?.products}
            scroll={{ x: 'auto' }}
            totalLenght={customerData?.count}
            onRow={(record) => {
              return {
                onClick: (event) => {
                  const { className } = event?.target;
                  if (!className?.includes('table-action-btn')) {
                    navigate(`/subscriptions/customer/${record?._id}`, {
                      state: {
                        record,
                        id: record?._id,
                        filtersData: handleStateByPassData(queryParams),
                        searchValue: search,
                      },
                    });
                  }
                },
                onMouseEnter: handleRowHover,
                className: `custom-row-class ${recordId === record?._id ? 'highlighted-row' : ''}`,
              };
            }}
            pagingation={{
              handlePageChange: handlePagePageChange,
              perPageSize: queryParams?.limit,
              page: queryParams?.currentPage,
            }}
          />
        </div>
      </div>

      <Modal
        open={deleteCustomerModal}
        centered
        width={400}
        onCancel={() => {
          setDeleteCustomerModal(false);
        }}
        footer={false}
        okButtonProps={{ loading: isCustomerDeleting }}
        destroyOnClose>
        <header className='font-semibold text-base mb-4'>Delete Customer</header>
        <div className='mb-8'>
          Are you sure you want to delete this Customer<span>{'?'}</span>
        </div>
        <div className='flex mt-4 popupConfirmBtn'>
          <Button
            onClick={() => {
              setDeleteCustomerModal(false);
            }}
            block
            className='rounded-lg bg-plain'>
            Cancel
          </Button>
          <Button className='DeleteBtn ml-4 rounded-lg' block onClick={() => handleDeleteCustomer(customerModalData?._id)} loading={isCustomerDeleting}>
            Confirm
          </Button>
        </div>
      </Modal>
      <Modal trigger='click' centered open={open} width={window.innerWidth < 768 ? 350 : 600} footer={null} destroyOnClose onCancel={handleOpen}>
        <div className='mobileVersion'>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            <span className='createIcon'>
              <DiffOutlined />
            </span>
          </div>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            {!isEdit ? 'Add new Customer' : 'Update Customer'}
          </div>
          {dataLoading ? (
            <div style={{ width: '100%' }}>
              <Skeleton active />
            </div>
          ) : (
            <CreateCustomer handleOpen={handleOpen} refetch={refetch} customerData={customerModalData} isEdit={isEdit} />
          )}
        </div>
      </Modal>
    </>
  );
};
